import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Modal from 'react-modal';
import { TEXT_COLOR_TYPE } from '../types.js';
import { mergeStyles } from 'wix-rich-content-common';
import { EditorState, getSelectionStyles, InlineToolbarButton, ClickOutside, FORMATTING_BUTTONS } from 'wix-rich-content-editor-common';
import TextColorPanel from './TextColorPanel.js';
import { DEFAULT_STYLE_SELECTION_PREDICATE, PANEL_WIDTH } from '../constants.js';
import { styleMapper } from '../text-decorations-utils.js';
import 'wix-rich-content-plugin-commons';
import '../color-scheme-utils.js';
import 'lodash';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".IkK-d{display:inline-table;height:89px;max-width:360px;min-height:89px;min-width:184px;position:absolute;width:184px}[dir] .IkK-d{background:#fff;border:1px solid #ededed;border-radius:6px;margin:0}.HWOuk{bottom:0;display:inline-table;max-width:640px;position:fixed;top:unset;width:100%;z-index:8}[dir] .HWOuk{margin:0 auto;transform:unset}[dir=ltr] .HWOuk{box-shadow:4px 0 4px 0 rgba(0,0,0,.1),0 0 8px 0 rgba(0,0,0,.1);left:0;right:0}[dir=rtl] .HWOuk{box-shadow:-4px 0 4px 0 rgba(0,0,0,.1),0 0 8px 0 rgba(0,0,0,.1);left:0;right:0}[dir=ltr] .HWOuk:focus{box-shadow:4px 0 4px 0 rgba(0,0,0,.1),0 0 8px 0 rgba(0,0,0,.1)}[dir=rtl] .HWOuk:focus{box-shadow:-4px 0 4px 0 rgba(0,0,0,.1),0 0 8px 0 rgba(0,0,0,.1)}.HAQ5m,.Ys5Np{bottom:0;position:fixed;top:0;z-index:8}[dir] .HAQ5m,[dir] .Ys5Np{background-color:transparent}[dir=ltr] .HAQ5m,[dir=ltr] .Ys5Np{left:0;right:0}[dir=rtl] .HAQ5m,[dir=rtl] .Ys5Np{left:0;right:0}._90MPP{position:relative}[dir=ltr] ._90MPP{margin-right:10px}[dir=rtl] ._90MPP{margin-left:10px}._90MPP .FSfzt{max-width:360px;min-width:184px;position:absolute;top:50px}[dir] ._90MPP .FSfzt{background:#fff;border:1px solid #ededed;border-radius:2px}";
var styles = {"textColorModal":"IkK-d","textColorModal_mobile":"HWOuk","textColorModalOverlay":"Ys5Np","textColorModalOverlay_mobile":"HAQ5m","textColorPopup_button":"_90MPP","textColorPopup":"FSfzt"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class BaseTextColor extends Component {
  constructor(props) {
    super(props);
    __publicField(this, "openPanel", () => {
      const { isMobile, setKeepOpen, config, pluginParams } = this.props;
      if (!isMobile) {
        setKeepOpen && setKeepOpen(true);
      }
      const settings = config[pluginParams.type];
      let position = {};
      if (settings.positionPicker) {
        position = settings.positionPicker(this.buttonRef, PANEL_WIDTH);
      } else {
        const { bottom, left } = this.buttonRef.current.getBoundingClientRect();
        position = { left: left - PANEL_WIDTH / 2, top: bottom };
      }
      this.setState({ isPanelOpen: true, panelLeft: position.left, panelTop: position.top });
    });
    __publicField(this, "closePanel", (editorState) => {
      this.setState({ isPanelOpen: false });
      this.props.setKeepOpen(false);
      this.preserveSelectionState(editorState);
    });
    this.state = { showPanel: false };
    this.styles = mergeStyles({ styles, theme: props.theme });
    this.buttonRef = props.buttonRef;
    this.styleMapper = styleMapper(props.pluginParams.type);
  }
  static getModalParent() {
    var _a;
    return (_a = document == null ? void 0 : document.querySelector(".DraftEditor-root")) == null ? void 0 : _a.parentNode;
  }
  preserveSelectionState(newEditorState) {
    const { setEditorState, getEditorState } = this.props;
    const editorState = getEditorState();
    const selection = editorState.getSelection();
    setEditorState(EditorState.forceSelection(newEditorState || editorState, selection));
  }
  get isActive() {
    const { config, pluginParams, getEditorState } = this.props;
    const settings = config[pluginParams.type] || {};
    const styleSelectionPredicate = pluginParams.predicate(
      settings.styleSelectionPredicate || DEFAULT_STYLE_SELECTION_PREDICATE
    );
    return getSelectionStyles(getEditorState(), styleSelectionPredicate).length > 0;
  }
  render() {
    const {
      theme,
      helpers,
      isMobile,
      t,
      tabIndex,
      setEditorState,
      getEditorState,
      setKeepOpen,
      config,
      uiSettings,
      pluginParams,
      toolbarName
    } = this.props;
    const settings = config[pluginParams.type];
    const { isPanelOpen, panelTop, panelLeft } = this.state;
    const tooltip = t(pluginParams.toolTip);
    const buttonStyles = {
      button: theme.inlineToolbarButton,
      buttonWrapper: theme.inlineToolbarButton_wrapper,
      icon: theme.inlineToolbarButton_icon,
      active: theme.inlineToolbarButton_active
    };
    const modalStyle = {
      content: isMobile ? {
        top: "unset",
        left: 0,
        backgroundColor: "white"
      } : {
        top: panelTop,
        left: panelLeft,
        marginTop: 15,
        borderRadius: "var(--ricos-settings-whitebox-border-radius, 2px)",
        width: 184
      }
    };
    const { TEXT_COLOR, TEXT_HIGHLIGHT } = FORMATTING_BUTTONS;
    const formattingButtonName = pluginParams.type === TEXT_COLOR_TYPE ? TEXT_COLOR : TEXT_HIGHLIGHT;
    if (isMobile || toolbarName !== "StaticTextToolbar" || !settings.inlinePopups) {
      return /* @__PURE__ */ React.createElement(
        InlineToolbarButton,
        {
          onClick: this.openPanel,
          isActive: this.isActive,
          helpers,
          theme: __spreadValues(__spreadValues({}, theme), buttonStyles),
          isMobile,
          tooltipText: tooltip,
          dataHook: pluginParams.dataHook,
          formattingButtonName,
          tabIndex,
          icon: pluginParams.icon,
          forwardRef: this.buttonRef,
          pluginType: pluginParams.type
        },
        /* @__PURE__ */ React.createElement(
          Modal,
          {
            onRequestClose: () => this.closePanel(),
            isOpen: isPanelOpen,
            parentSelector: BaseTextColor.getModalParent,
            className: classNames({
              [this.styles.textColorModal]: !isMobile,
              [this.styles.textColorModal_mobile]: isMobile
            }),
            overlayClassName: classNames({
              [this.styles.textColorModalOverlay]: !isMobile,
              [this.styles.textColorModalOverlay_mobile]: isMobile
            }),
            style: modalStyle,
            ariaHideApp: false
          },
          /* @__PURE__ */ React.createElement(
            TextColorPanel,
            {
              t,
              isMobile,
              theme,
              closeModal: this.closePanel,
              editorState: getEditorState(),
              setEditorState,
              settings,
              uiSettings,
              setKeepToolbarOpen: setKeepOpen,
              styleMapper: this.styleMapper,
              predicate: pluginParams.predicate,
              defaultColor: pluginParams.defaultColor,
              colorPickerHeaderKey: pluginParams.colorPickerHeaderKey
            }
          )
        )
      );
    } else {
      return /* @__PURE__ */ React.createElement("div", { className: styles.textColorPopup_button }, /* @__PURE__ */ React.createElement(
        InlineToolbarButton,
        {
          onClick: this.openPanel,
          isActive: this.isActive,
          helpers,
          theme: __spreadValues(__spreadValues({}, theme), buttonStyles),
          isMobile,
          tooltipText: tooltip,
          dataHook: pluginParams.dataHook,
          formattingButtonName,
          tabIndex,
          icon: pluginParams.icon,
          forwardRef: this.buttonRef,
          pluginType: pluginParams.type
        },
        isPanelOpen && /* @__PURE__ */ React.createElement("div", { className: styles.textColorPopup }, /* @__PURE__ */ React.createElement(ClickOutside, { onClickOutside: () => this.closePanel() }, /* @__PURE__ */ React.createElement(
          TextColorPanel,
          {
            t,
            isMobile,
            theme,
            closeModal: this.closePanel,
            editorState: getEditorState(),
            setEditorState,
            settings,
            uiSettings,
            setKeepToolbarOpen: setKeepOpen,
            styleMapper: this.styleMapper,
            predicate: pluginParams.predicate,
            defaultColor: pluginParams.defaultColor,
            colorPickerHeaderKey: pluginParams.colorPickerHeaderKey
          }
        )))
      ));
    }
  }
}
BaseTextColor.propTypes = {
  getEditorState: PropTypes.func.isRequired,
  setEditorState: PropTypes.func.isRequired,
  onExtendContent: PropTypes.func.isRequired,
  onOverrideContent: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  textColorModal: PropTypes.bool,
  helpers: PropTypes.object,
  keyName: PropTypes.string,
  anchorTarget: PropTypes.string,
  relValue: PropTypes.string,
  t: PropTypes.func,
  tabIndex: PropTypes.number,
  toolbarName: PropTypes.string,
  uiSettings: PropTypes.object,
  config: PropTypes.object,
  setKeepOpen: PropTypes.func,
  pluginParams: PropTypes.object.isRequired,
  buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.func })])
};
BaseTextColor.defaultProps = {
  setKeepOpen: () => {
  }
};

export { BaseTextColor as default };
//# sourceMappingURL=BaseTextColor.js.map
